/**
 * Data transformation utilities for converting between different data formats.
 * Used across the app for Directus data processing, route-to-nav conversion, and string formatting.
 */

import type { RouteRecordNormalized, RouteRecordRaw } from '#vue-router'

/** Deep clones an object via JSON serialization (does not preserve functions, dates, or circular refs) */
export function deepClone<T extends object>(obj: T) {
  return JSON.parse(JSON.stringify(obj)) as T
}

/**
 * Converts an array of items into a keyed object for O(1) lookups.
 * @param theItems - Array of objects to index
 * @param key - Property to use as the index key (defaults to 'id')
 */
export function listToIndex<T extends Record<string, any>>(theItems?: T[] | null, key: keyof T = 'id') {
  const itemIndex: Record<string, T> = {}
  if (theItems) {
    theItems.forEach((item) => {
      if (item[key] !== undefined)
        itemIndex[item[key]] = item
    })
  }
  return itemIndex
}

/**
 * Converts Directus junction table rows into a keyed object.
 * Junction tables use numeric IDs, so the index is Record<number, T> instead of Record<string, T>.
 * @param theJunctions - Array of junction table rows
 * @param key - Property to use as the index key (defaults to 'id')
 */
export function transformJunctionDataToIndex<T extends Record<string, any>>(theJunctions?: T[] | null, key: keyof T = 'id') {
  const junctionIndex: Record<number, T> = {}
  if (theJunctions) {
    theJunctions.forEach((item) => {
      if (item[key] !== undefined)
        junctionIndex[item[key]] = item
    })
  }
  return junctionIndex
}

/** Splits a URL path into an array of slugs, stripping leading slash and splitting on / or # */
export function splitPath(path: string) {
  if (path.endsWith('/'))
    path = path.slice(0, -1)
  return path.substring(1).split(/[/#]/)
}

/** Converts a Vue Router route record into a NavItem, pulling metadata from route.meta */
export function transformRouteToNavItem(route: RouteRecordNormalized | RouteRecordRaw): NavItem {
  return {
    to: route.path,
    name: (route.name ?? route.path.replaceAll('/', '-')) as string,
    title: (route.meta?.title ?? route.name) as string,
    label: route.meta?.label as string,
    icon: route.meta?.icon as string,
    order: route.meta?.order as number,
    children: !route.children || route.children?.length > 0 ? route.children?.map(child => child.name as string) : undefined,
  }
}

/** Converts a snake_case field key to Title Case (e.g., 'contact_name' -> 'Contact Name') */
export function formatFieldKey(key: string) {
  const underscoresToArray = key.split('_')
  const capitalized = underscoresToArray.map(str => [str[0]?.toUpperCase(), str.slice(1)].join('')).join(' ')
  return capitalized
}
